import type { MxfRewrapItem, MxfRewrapItemProvider, MxfRewrapItemRequest } from '@mimir/common/transcoding/mxf/rewrapInputs';
import { resolveMxfRewrapItemRange } from '@mimir/common/transcoding/mxf/rewrapInputs';
import type { BackendClient } from '@mimir/common/util/backendClient';
import type { LoadedItem } from '@mimir/common/dto/searchResultHit';
import { api_getExternalHighresInfo, api_getItem, api_getItemOfflineStorageInfo } from './api';
import { TechnicalVideoDataField, getTechnicalMetadataValue } from '@mimir/common/util/technicalMetadata';
import type { FuzzyValue } from '@mimir/common/util/fuzzyNumbers';
import { divideFuzzy, integerToFuzzy } from '@mimir/common/util/fuzzyMath';
import { getFrameDurationFromFrameRateApproximation } from '@mimir/common/util/framerate';
import { PromiseUtil } from '@mimir/common/async/async';
import type { LocalMediaInfo, LocalMediaPathProvider, LocalMediaTiming } from '@mimir/common/types/localMediaPathProvider';
import type { ResourceLocation } from '@mimir/common/io/resource';
import type { PartialHighres } from '@mimir/common/types/partialHighres';
import type { Vfs } from '@mimir/common/types/vfs';
import type { StructuredLogger } from '@mimir/primitive/structuredLogger';
import { readableFileStream } from '@mimir/common/io/fileStream';
import { readWholeStream } from '@mimir/common/util/readableStream';
import { mxfToIndex } from '@mimir/common/transcoding/mxf/mxfToIndex';
import { invariant } from '@mimir/common/util/invariant';
import { HighresUrlProviderWithClient } from './itemUrlProvider';
import { makeStaticUrlProvider } from '@mimir/common/io/urlProvider';

// Files retrieved on demand for a specific rewrap (external highres segments, offline storage
// restores) are ad-hoc mxf files that were never indexed by the normal ingest pipeline, so
// there's nothing to look up under item.indexUrl or a local .idx file - only present when the
// provider can also resolve locally-retrieved partial highres, since generating an index
// requires reading the file's own bytes from disk.
export interface DefaultMxfRewrapItemProviderLocalResolution {
  localMediaPathProvider: LocalMediaPathProvider,
  vfs: Vfs,
  logger: StructuredLogger,
}

export class DefaultMxfRewrapItemProvider implements MxfRewrapItemProvider {
  constructor(
    private backendClient: BackendClient,
    private localResolution: DefaultMxfRewrapItemProviderLocalResolution | null,
  ) { }

  async getRewrapItems(requests: MxfRewrapItemRequest[]): Promise<MxfRewrapItem[]> {
    const promiseUtil = new PromiseUtil<MxfRewrapItem>();
    for (const request of requests) {
      promiseUtil.push(this.fetchRewrapItem(request));
    }
    return await promiseUtil.waitForAll();
  }

  private async fetchRewrapItem(
    request: MxfRewrapItemRequest
  ): Promise<MxfRewrapItem> {
    const { itemId, range } = request;
    const item = await api_getItem(this.backendClient, itemId);
    const timing = resolveMxfRewrapItemRange(range, item.media?.segmentDuration, item.id);

    const approximateFrameRate = getTechnicalMetadataValue<number>(item.technicalMetadata, TechnicalVideoDataField.FrameRate);
    if (approximateFrameRate === null) {
      throw new Error(`Item ${item.id} is not compatible with MXF rewrap: no frame rate`);
    }

    const frameDuration : FuzzyValue = getFrameDurationFromFrameRateApproximation(approximateFrameRate);
    const frameRate = divideFuzzy(integerToFuzzy(1), frameDuration);

    let highRes : ResourceLocation | null = null;

    if (item.highRes !== undefined) {
      highRes = {
        type: 'http',
        provider: new HighresUrlProviderWithClient(itemId, item.highRes, this.backendClient),
      };
    }

    // Defaults to the requested range, matching a whole-item HTTP/local resolution; overridden
    // below only when a partial-highres file is actually picked, since that file's frame 0 is
    // not the item's frame 0.
    let effectiveTiming : LocalMediaTiming = timing;
    // Set below only when highRes was resolved to one of partialHighresInfo's own retrieved
    // files, rather than a complete local file - that file has no pre-existing index (it was
    // never indexed by the normal ingest pipeline) and, for external highres specifically, is
    // known to always start at origin 0 rather than the item's technicalMetadata.MxfOrigin.
    let resolvedPartialHighresEntry : PartialHighres | null = null;

    if (this.localResolution !== null) {
      const { localMediaPathProvider } = this.localResolution;
      const partialHighresInfo = await this.getPartialHighresInfo(item, frameRate);
      const localMediaInfo : LocalMediaInfo = {
        type: 'highres',
        itemId: item.id,
        ingest: item.ingest ?? null,
        ingestSourceFullPath: item.ingestSourceFullPath ?? null,
        ingestSourceS3Bucket: item.ingestSourceS3Bucket ?? null,
        originalFileName: item.originalFileName ?? null,
        partialHighresInfo: partialHighresInfo.length > 0 ? partialHighresInfo : null,
      };

      const localResult = await localMediaPathProvider.getLocalMediaPath(localMediaInfo, timing);
      if (localResult !== null) {
        invariant(localResult.effectiveTimeRange !== null, 'expected effectiveTimeRange for a non-null timing query');
        highRes = { type: 'file', filename: localResult.path };
        effectiveTiming = localResult.effectiveTimeRange;
        resolvedPartialHighresEntry = partialHighresInfo.find((p) => p.path === localResult.path) ?? null;
      }
    }

    if (highRes === null) {
      throw new Error(`Item ${item.id} is not compatible with MXF rewrap: no high res URL and media is not found on local disk.`);
    }

    const origin = resolvedPartialHighresEntry?.type === 'segment_range'
      ? 0
      : (getTechnicalMetadataValue<number>(item.technicalMetadata, TechnicalVideoDataField.MxfOrigin) ?? 0);

    let indexLocation : ResourceLocation | null = null;

    if (resolvedPartialHighresEntry !== null) {
      invariant(this.localResolution !== null, 'expected localResolution to resolve a partial highres entry');
      const { vfs, logger } = this.localResolution;
      const indexBytes = await readWholeStream(readableFileStream(vfs, resolvedPartialHighresEntry.path).pipeThrough(mxfToIndex(logger)));
      indexLocation = { type: 'memory', bytes: indexBytes };
    } else {
      if (item.indexUrl !== undefined) {
        indexLocation = { type: 'http', provider: makeStaticUrlProvider(item.indexUrl) };
      }

      if (this.localResolution !== null) {
        const localIndexInfo : LocalMediaInfo = {
          type: 'index',
          itemId: item.id,
        };
        const localIndexResult = await this.localResolution.localMediaPathProvider.getLocalMediaPath(localIndexInfo, null);
        if (localIndexResult !== null) {
          indexLocation = { type: 'file', filename: localIndexResult.path };
        }
      }
    }

    if (indexLocation === null) {
      throw new Error(`Item ${item.id} is not compatible with MXF rewrap: no index URL and index file is not found on local disk.`);
    }

    const mxfRewrapItem : MxfRewrapItem = {
      id: item.id,
      highRes,
      index: indexLocation,
      frameRate,
      compatibilityHash: item.rewrapCompatibilityHash ?? null,
      origin,
      effectiveTiming,
    };

    return mxfRewrapItem;
  }

  // Mirrors BackendOpsMediaApi.getItem(): builds the set of locally-available portions of an
  // item's highres, from whichever of external highres retrieval / offline storage restore
  // applies to this item.
  private async getPartialHighresInfo(
    item: Pick<LoadedItem, 'id' | 'hasMediaIngestIntegrationExternalHighres' | 'isInOfflineStorage' | 'media'>,
    frameRate: FuzzyValue,
  ): Promise<PartialHighres[]> {
    const partialHighresInfo : PartialHighres[] = [];

    if (item.hasMediaIngestIntegrationExternalHighres && item.media) {
      const externalHighresInfo = await api_getExternalHighresInfo(this.backendClient, item.id);
      for (const segment of externalHighresInfo?.retrievedSegments ?? []) {
        partialHighresInfo.push({
          type: 'segment_range',
          startSegment: segment.startSegment,
          endSegment: segment.endSegment,
          path: segment.filePath,
          segmentDuration: item.media.segmentDuration,
        });
      }
    }

    if (item.isInOfflineStorage) {
      const offlineStorageInfo = await api_getItemOfflineStorageInfo(this.backendClient, item.id);
      for (const range of offlineStorageInfo?.restoredRanges ?? []) {
        partialHighresInfo.push({
          type: 'frame_range',
          startFrame: range.startFrame,
          endFrame: range.endFrame,
          path: range.filePath,
          frameRate,
        });
      }
    }

    return partialHighresInfo;
  }
}

